import { StyleSheet, View } from 'react-native';

import { Card } from '@/components/Card';
import { StepNumber } from '@/components/StepNumber';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';

export interface HowItWorksStep {
  title: string;
  body: string;
}

export interface HowItWorksStepsProps {
  steps: readonly HowItWorksStep[];
  /** Card header. Rendered like WhoClickedCard's so sibling sections match. */
  title?: string;
  testID?: string;
}

/**
 * Numbered explainer card. The numbers are load-bearing here — these are a
 * real sequence (share → they join → they finish a deal → you both get
 * paid), not decoration, so the ordinal tells the reader something the
 * copy alone doesn't.
 */
export function HowItWorksSteps({ steps, title, testID }: HowItWorksStepsProps) {
  return (
    <Card style={styles.card} testID={testID}>
      {title ? (
        // Body font, not the display face: WhoClickedCard sets the pattern
        // for a section header, and FK Screamer reads cramped this small.
        <Text variant="bodyBold" color="deepGreen" style={styles.header}>
          {title}
        </Text>
      ) : null}
      {steps.map((step, i) => (
        <View key={step.title} style={styles.row}>
          <StepNumber number={i + 1} />
          <View style={styles.text}>
            <Text style={styles.title}>{step.title}</Text>
            <Text style={styles.body}>{step.body}</Text>
          </View>
        </View>
      ))}
    </Card>
  );
}

const styles = StyleSheet.create({
  header: {
    fontSize: fontSizes.lg,
    lineHeight: 24,
  },
  card: {
    padding: spacing.lg,
    gap: spacing.base,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  text: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.base,
    color: colors.deepGreen,
  },
  body: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    lineHeight: 22,
    color: colors.text,
  },
});
